import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import Hero from './components/Hero';
import CategorySection from './components/CategorySection';
import FeaturesSection from './components/FeaturesSection';
import PillarSection from './components/PillarSection';
import FaqSection from './components/FaqSection';
import { MAIN_SITE_URL } from '@/mocks/categories';
import JsonLd from '@/components/base/JsonLd';
import { buildWebSiteSchema } from '@/seo/schema';

export default function Home() {
  const { t } = useTranslation();
  const location = useLocation();
  const websiteSchema = buildWebSiteSchema(t('home.metaDescription'));

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.replace('#', ''));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 50);
      }
    }
  }, [location]);

  return (
    <>
      <title>{t('home.metaTitle')}</title>
      <meta name="description" content={t('home.metaDescription')} />
      <JsonLd data={websiteSchema} />
      <div className="min-h-screen bg-background-50">
      <Header />
      <main>
        <Hero />
        <CategorySection />

        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="max-w-6xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">
                {t('home.what.title')}
              </h2>
              <p className="mt-5 text-sm md:text-base text-foreground-700 leading-relaxed">
                {t('home.what.p1')}
              </p>
              <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                {t('home.what.p2')}
              </p>
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors"
              >
                {t('home.what.cta')}
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
            <div className="rounded-lg overflow-hidden border border-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20laptop%20with%20an%20open%20spreadsheet%20next%20to%20a%20small%20ceramic%20mug%20and%20a%20tiny%20succulent%20plant%20on%20a%20bright%20oak%20desk%2C%20soft%20morning%20light%2C%20fresh%20minimalist%20western%20editorial%20style%2C%20high%20detail&width=800&height=600&seq=usfans-guide-2&orientation=landscape"
                alt="USFans Spreadsheet organized category guide"
                title="USFans Spreadsheet guide"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        <FeaturesSection />

        <PillarSection />

        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="rounded-2xl border border-background-200 bg-background-50 p-8 md:p-12 text-center">
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary-100 text-primary-700">
                <i className="ri-book-open-line text-xl"></i>
              </span>
              <h2 className="mt-5 text-2xl md:text-3xl font-bold text-foreground-950">
                {t('home.popular.title')}
              </h2>
              <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
                {t('home.popular.subtitle')}
              </p>
              <Link
                to="/usfans-spreadsheet-guide"
                className="mt-6 inline-flex items-center gap-2 px-7 py-3 rounded-md bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                {t('home.popular.cta')}
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        <FaqSection />

        <section className="w-full bg-accent-500 py-14 md:py-20">
          <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-background-50">
              {t('home.finalCta.title')}
            </h2>
            <p className="mt-3 text-sm md:text-base text-background-100/90 max-w-xl mx-auto">
              {t('home.finalCta.subtitle')}
            </p>
            <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3 rounded-md bg-background-50 text-foreground-950 font-semibold text-sm hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                {t('home.finalCta.primary')}
                <i className="ri-arrow-right-up-line"></i>
              </a>
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3 rounded-md border border-background-50/40 text-background-50 font-semibold text-sm hover:bg-background-50/10 transition-colors whitespace-nowrap"
              >
                {t('home.finalCta.secondary')}
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      </div>
    </>
  );
}